import { Link } from "@tanstack/react-router";
import { ChevronDown, MessageSquare, Send, Sparkles, StopCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { useT } from "../../hooks/useT";
import { AddToNotebookPopover } from "../notebooks/AddToNotebookPopover";
import { ChatMessageMarkdown } from "./ChatMessageMarkdown";
import { useSseChat } from "./useSseChat";

const SCOPES = [
  { id: "official", label: "Toda la documentación oficial" },
  { id: "project", label: "Proyecto: netbox" },
  { id: "selection", label: "Selección actual" },
  { id: "notebook", label: "Notebook: NetBox onboarding" },
  { id: "all", label: "Todo (incluye apuntes)" },
];

export function ChatPage() {
  const { messages, isStreaming, send, cancel } = useSseChat();
  const [input, setInput] = useState("");
  const [scope, setScope] = useState("official");
  const t = useT();

  function onSubmit(): void {
    if (!input.trim() || isStreaming) return;
    send({ question: input.trim(), scope }).catch(() => undefined);
    setInput("");
  }

  return (
    <div className="flex h-full overflow-hidden">
      <aside className="w-56 overflow-y-auto border-r border-(--border-subtle) bg-(--bg-elevated) p-3">
        <header className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
          Sesiones
        </header>
        <ul className="space-y-1 text-sm">
          <li className="rounded px-2 py-1 bg-(--bg-active) font-medium">Nueva sesión</li>
          <li className="rounded px-2 py-1 hover:bg-(--bg-hover) text-(--text-secondary)">
            netbox depl. (ayer)
          </li>
          <li className="rounded px-2 py-1 hover:bg-(--bg-hover) text-(--text-secondary)">
            cf-access (2d)
          </li>
        </ul>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center gap-2 border-b border-(--border-subtle) bg-(--bg-elevated) px-4 py-2">
          <h2 className="font-semibold flex items-center gap-2">
            <MessageSquare size={16} /> Chat documental
          </h2>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" size="sm">
                Scope: <strong className="ml-1">{SCOPES.find((s) => s.id === scope)?.label}</strong>{" "}
                <ChevronDown size={12} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-1">
              {SCOPES.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setScope(s.id)}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-(--bg-hover)"
                >
                  <span>{s.label}</span>
                  {s.id === scope && <span className="text-(--accent-500)">●</span>}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {messages.length === 0 && (
            <div className="mx-auto max-w-2xl py-12 text-center">
              <Sparkles size={28} className="mx-auto mb-3 text-(--accent-500)" />
              <h3 className="text-xl font-semibold">{t("chat.welcome")}</h3>
              <p className="mt-1 text-sm text-(--text-secondary)">
                Pregunta cualquier cosa sobre la documentación. Las respuestas vendrán con citas
                verificables.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2 text-left text-sm">
                {[
                  "¿Cómo desplegar netbox en staging?",
                  "Diferencias entre cf-access y service tokens",
                  "Pasos para configurar TLS en Workers",
                  "Errores comunes de Tailscale + Cloudflare",
                ].map((q) => (
                  <button
                    type="button"
                    key={q}
                    onClick={() => setInput(q)}
                    className="rounded-md border border-(--border-subtle) bg-(--bg-elevated) p-3 text-(--text-secondary) hover:bg-(--bg-hover)"
                  >
                    "{q}"
                  </button>
                ))}
              </div>
            </div>
          )}

          <ul className="mx-auto max-w-3xl space-y-4">
            {messages.map((m) => (
              <li
                key={m.id}
                className={m.role === "user" ? "" : "rounded-lg bg-(--bg-elevated) px-4 py-3"}
              >
                <header className="mb-1 text-xs font-semibold text-(--text-tertiary)">
                  {m.role === "user" ? "Tú" : "DocuStudio"}
                </header>
                <div className="text-sm">
                  {m.role === "user" ? (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  ) : m.content ? (
                    <>
                      <ChatMessageMarkdown text={m.content} />
                      {m.streaming && (
                        <span className="ml-0.5 inline-block w-1 animate-pulse bg-(--accent-500)">
                          ▍
                        </span>
                      )}
                    </>
                  ) : m.streaming ? (
                    <span className="text-(--text-tertiary)">pensando…</span>
                  ) : null}
                </div>
                {m.sources && m.sources.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.sources.map((s) => (
                      <Link
                        key={s.chunk_id}
                        to="/docs/$docId"
                        params={{ docId: s.doc_id }}
                        className="inline-flex items-center gap-1 rounded-full bg-(--bg-base) px-2 py-0.5 text-xs hover:bg-(--bg-hover)"
                      >
                        📘 {s.path.split("/").slice(-1)[0]}
                        {s.heading_path && s.heading_path.length > 0
                          ? ` §${s.heading_path[s.heading_path.length - 1]}`
                          : ""}
                        <Badge variant="muted" className="text-[9px]">
                          {s.score.toFixed(2)}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                )}
                {m.role === "assistant" && !m.streaming && (
                  <div className="mt-2 flex gap-1 text-xs">
                    <AddToNotebookPopover
                      item={{
                        item_type: "selection",
                        selected_text: m.content,
                        note_text: m.content,
                        title: m.content.slice(0, 80),
                      }}
                      trigger={
                        <Button variant="ghost" size="sm">
                          📓 Apuntes
                        </Button>
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigator.clipboard.writeText(m.content)}
                    >
                      📋 Copiar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toast.message("Regenerar próximamente")}
                    >
                      🔄 Regenerar
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <footer className="border-t border-(--border-subtle) bg-(--bg-elevated) p-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            className="mx-auto flex max-w-3xl gap-2"
          >
            <Input
              placeholder={t("chat.input_placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isStreaming}
              autoFocus
            />
            {isStreaming ? (
              <Button variant="secondary" size="md" type="button" onClick={cancel}>
                <StopCircle size={14} /> Cancelar
              </Button>
            ) : (
              <Button variant="primary" size="md" type="submit" disabled={!input.trim()}>
                <Send size={14} /> Enviar
              </Button>
            )}
          </form>
          <p className="mx-auto mt-1 max-w-3xl text-[10px] text-(--text-tertiary)">
            ⌘↵ enviar · Esc cancelar · scope=<strong>{scope}</strong>
          </p>
        </footer>
      </main>
    </div>
  );
}
